// /src/i18n/translationService.js
// Database-driven translation service for OurNet
//
// Loads all approved translations for a locale from Supabase via RPC.
// Returns plain objects — React state management is handled by useTranslation.js.
//
// Uses supabaseFetch.rpc() — NOT supabase.from() (which hangs).

import { supabaseFetch } from '../lib/supabase';

/**
 * Fetch all English source strings from database.
 * @returns {object} - Flat object { 'auth.login_title': 'Please log in.', ... }
 */
export async function fetchEnglishStrings() {
  try {
    const { data, error } = await supabaseFetch.rpc('get_english_strings');

    if (error) {
      console.error('Failed to load English strings:', error);
      return {};
    }

    const result = (data && typeof data === 'object') ? data : {};
    console.log(`🌐 Loaded ${Object.keys(result).length} English source strings`);
    return result;
  } catch (err) {
    console.error('Error loading English strings:', err);
    return {};
  }
}

/**
 * Fetch all approved translations for a locale.
 * For 'en', returns empty object (English uses source strings directly).
 *
 * @param {string} locale - e.g. 'en', 'pt-PT', 'fr-FR'
 * @returns {object} - Flat object { 'auth.login_title': 'Iniciar sessão', ... }
 */
export async function fetchTranslations(locale) {
  if (locale === 'en') {
    return {};
  }

  try {
    const { data, error } = await supabaseFetch.rpc('get_translations', {
      target_locale: locale
    });

    if (error) {
      console.error(`Failed to load translations for ${locale}:`, error);
      return {};
    }

    if (data && typeof data === 'object' && !Array.isArray(data)) {
      console.log(`🌐 Loaded ${Object.keys(data).length} translations for ${locale}`);
      return data;
    } else {
      console.warn(`🌐 Unexpected data format for ${locale}:`, data);
      return {};
    }
  } catch (err) {
    console.error(`Error loading translations for ${locale}:`, err);
    return {};
  }
}

/**
 * Look up a translation by key from provided caches.
 *
 * @param {string} key - Full key like 'auth.login_title'
 * @param {string} locale - Current locale
 * @param {object} translations - Translated strings cache
 * @param {object} english - English source strings cache
 * @param {object} variables - Interpolation variables { name: 'value' }
 * @returns {string}
 */
export function resolveTranslation(key, locale, translations, english, variables = {}) {
  let text;

  if (locale === 'en') {
    text = english[key];
  } else {
    text = translations[key] || english[key];
  }

  if (!text) {
    return key;
  }

  // Handle {variable} and {{variable}} interpolation
  if (variables && Object.keys(variables).length > 0) {
    Object.entries(variables).forEach(([varName, value]) => {
      text = text.replace(new RegExp(`(\\{\\{${varName}\\}\\}|\\{${varName}\\})`, 'g'), String(value));
    });
  }

  return text;
}
